import React, { useEffect, useState, useMemo } from 'react';
import { divisionLevelHeatMap } from '../../../../services/api-services';
import { toPng } from 'html-to-image';
import Remark from '../Remark';
import DownloadGraphButton from '../../../../user/pages/page/DownloadGraphButton';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DivisionLevelHeatmap = ({ selectedCompanyId, graphName, myhandleExport }) => {
    const [divisionHeatmapData, setDivisionHeatmapData] = useState([]);
    const [remark, setRemark] = useState("");
    const [remarkId, setRemarkId] = useState(null);
    const [isAttempt, setIsAttempt] = useState("");
    const [colorRanges, setColorRanges] = useState([]);

    useEffect(() => {
        const getHeatmapDivisionData = async () => {
            try {
                const response = await divisionLevelHeatMap(selectedCompanyId, "department", graphName);
                if (response?.data?.responseCode === 200) {
                    setRemark(response?.data?.remark || "");
                    setIsAttempt(response?.data?.isAttempt ?? 0);
                    setRemarkId(response?.data?.remarkId || null);
                    setDivisionHeatmapData(response?.data?.responseBody || []);
                    setColorRanges(response?.data?.companyColorList || []);
                    setTimeout(() => generateHtmlImage("demographic_dashboard_division_level"), 500);
                }
            } catch (err) {
                console.error(err);
            }
        };
        getHeatmapDivisionData();
    }, [selectedCompanyId, graphName]);

    const generateHtmlImage = async (elementId) => {
        const node = document.getElementById(elementId);
        if (!node) return;
        try {
            const dataUrl = await toPng(node);
            const chartData = JSON.parse(localStorage.getItem("chartImageData")) || [];
            const updatedChartData = chartData.map((item) =>
                item.id === 'demographic_dashboard_division_level' ? { ...item, image: dataUrl, remark } : item
            );
            localStorage.setItem("chartImageData", JSON.stringify(updatedChartData));
        } catch (error) {
            console.error("Error generating image from HTML:", error);
        }
    };

    // Memoized function to get color for a percentage
    const getColorForPercentage = useMemo(() => (percentage) => {
        const range = colorRanges.find(range => percentage >= range.rangeFrom && percentage <= range.rangeTo);
        return range ? range.colorName : "#000000";
    }, [colorRanges]);

    // Memoized heatmap data filtering
    const processedHeatmapData = useMemo(() => {
        return divisionHeatmapData.map(division => {
            const validDimensions = division.dimensionOverallHeatmapChartResponseList?.filter(dim => dim.percentage >= 0) || [];
            return { ...division, validDimensions };
        }).filter(division => division.validDimensions.length > 0);
    }, [divisionHeatmapData]);

    const exportToExcel = (data, fileName = "Demographic Dashboard – (Division Level)") => {
        if (!data || !Array.isArray(data)) return;

        const title = ["Demographic Dashboard – (Division Level)"];
        const headers = [
            "Department Name",
            "Dimension Name",
            "Dimension Percentage",
            "Subdimension Name",
            "Subdimension Percentage"
        ];

        const rows = [];
        data.forEach(dept => {
            dept.dimensionOverallHeatmapChartResponseList?.forEach(dimension => {
                dimension.subDimensionOverallHeatmapChartResponseList?.forEach(sub => {
                    rows.push([
                        dept.name,
                        dimension.dimensionName,
                        dimension.percentage,
                        sub.subDimensionName,
                        sub.percentage
                    ]);
                });
            });
        });

        const worksheet = XLSX.utils.aoa_to_sheet([
            title,
            [],
            headers,
            ...rows
        ]);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Department Heatmap");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(blob, fileName);
    };

    return (
        <>
            <div className="container-fluid" id="demographic_dashboard_division_level">
                {processedHeatmapData.length > 0 ? (
                    processedHeatmapData.map((division, divisionIndex) => (
                        <div key={divisionIndex} className={`division-section mt-4 pdf-chart chart11-${divisionIndex}`} data-heading="Demographic Dashboard – (Division Level)" data-remark={remark}>
                            <div className="d-flex justify-content-between">
                                <h5>{division.name}</h5>
                                <div className="d-flex">
                                    <DownloadGraphButton chartID={`chart11-${divisionIndex}`} myhandleExport={myhandleExport} />

                                    <button className="px-3 py-2 mb-3 text-white bg-primary rounded border-0 ms-2" onClick={() => exportToExcel(divisionHeatmapData)}>Download Excel</button>
                                </div>
                            </div>

                            <div className="legend-container">
                                <div className="legend-row">
                                    {colorRanges.map((item, index) => (
                                        <div key={index} className="legend-item">
                                            <div className="legend-color" style={{ backgroundColor: item.colorName }}></div>
                                            <span className="legend-label">
                                                {item.rangeFrom}% - {item.rangeTo}%
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="header-row">
                                {division.validDimensions.map(dimension => (
                                    <div key={dimension.id} className="header-cell" style={{ backgroundColor: getColorForPercentage(dimension.percentage?.toFixed(2)) }}>
                                        <p>{dimension.dimensionName}</p>
                                        <p>{dimension.percentage.toFixed(2)}%</p>
                                    </div>
                                ))}
                            </div>

                            <div className="data-row">
                                {division.validDimensions.map(dimension => (
                                    <div key={dimension.id} className="column">
                                        {dimension.subDimensionOverallHeatmapChartResponseList?.map(subDimension => (
                                            <div key={subDimension.id} className="data-cell" style={{ backgroundColor: getColorForPercentage(subDimension.percentage?.toFixed(2)) }}>
                                                <p>{subDimension.subDimensionName}</p>
                                                <p>{subDimension.percentage?.toFixed(2)}%</p>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <p style={{ textAlign: "center", marginTop: "20px" }}>No data available to display.</p>
                )}
                <Remark
                    selectedCompanyId={selectedCompanyId}
                    graphName="DemographicDashboardDivision"
                    initialRemark={remark}
                    remarkId={remarkId}
                    isAttempt={isAttempt}
                    setRemarkprops={setRemark}
                />
            </div>
        </>

    );
};

export default DivisionLevelHeatmap;
